import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/dashboard';
import { OrganizationForm } from '../../components/organizations/OrganizationForm';
import { organizationService } from '../../services/organizationService';
import { CreateOrganizationDto, Organization } from '../../types/organization';
import { useAuth } from '../../contexts/AuthContext';
import { canUpdateOrganization } from '../../utils/permissions';
import { upsertInfluenceScores } from '../../services/influencesService';
import { Page, PageHeader, Skeleton } from '../../components/ui';

export const OrganizationEditPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        if (!id) return;
        setLoading(true);
        const data = await organizationService.getOrganization(id);
        setOrganization(data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load organization');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const [influenceScores, setInfluenceScores] = useState<{ categoryId: string; score: number }[]>([]);
  const handleUpdate = async (data: Partial<CreateOrganizationDto>) => {
    if (!id) return;
    setSubmitting(true);
    const updatePayload = {
      name: data.name,
      type: data.type,
      description: data.description,
      email: data.email,
      phone: data.phone,
      address: data.address,
      website: data.website,
      parentId: data.parentId,
      acronym: data.acronym,
      aliases: data.aliases,
    } as any;
    await organizationService.updateOrganization(id, updatePayload);
    if (influenceScores && influenceScores.length) {
      try { await upsertInfluenceScores('organization', id, influenceScores); } catch (e) {}
    }
    navigate(`/organizations/${id}`);
    setSubmitting(false);
  };

  const notAllowed = organization && !canUpdateOrganization(user, organization);

  return (
    <DashboardLayout>
      <Page>
        <div className="mx-auto max-w-3xl">
          <PageHeader
            back={{ to: id ? `/organizations/${id}` : '/organizations', label: organization?.name || 'Organization' }}
            title="Edit organization"
          />

          {loading ? (
            <div className="space-y-6" aria-busy="true" aria-label="Loading">
              {[3, 4, 1].map((rows, group) => (
                <div key={group}>
                  <Skeleton className="mb-3 h-4 w-20" />
                  <div className="grid grid-cols-1 gap-3 rounded-2xl border border-neutral-200 bg-white p-4 sm:grid-cols-2">
                    {Array.from({ length: rows }).map((_, i) => (
                      <div key={i} className={rows === 1 ? 'sm:col-span-2' : ''}>
                        <Skeleton className="mb-2 h-3 w-16" />
                        <Skeleton className="h-9 w-full rounded-xl" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>
          ) : notAllowed ? (
            <p role="alert" className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-700">
              You can't edit this organization.
            </p>
          ) : organization ? (
            <OrganizationForm
              organization={organization}
              onSubmit={handleUpdate}
              onCancel={() => navigate(`/organizations/${id}`)}
              influenceEntityId={id!}
              influenceScores={influenceScores}
              onInfluenceScoresChange={setInfluenceScores}
              isLoading={submitting}
            />
          ) : null}
        </div>
      </Page>
    </DashboardLayout>
  );
};
